import PropTypes from 'prop-types';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';

import DropdownField from '../../widgets/DropdownField';
import { CANADA_COUNTRY_CODE } from '../../../constants';

import PHONE_PREFIX_DATA from '../../../data/phonePrefix';

import styles from './PhoneNumberField.module.scss';

const PhoneNumberField = ({
  onPropertyChange,
  onInputBlur,
  value,
  name,
  label,
  countryId,
  abslength = 20,
  prefix = '',
  size = 'normal',
  inline = false,
  fullWidth = false,
  disabled = false,
  theme = 'light',
  className = '',
  dataHook = '',
  placeholder = '',
  error = '',
  borderFill = false,
}) => {
  const isCanadaUser = countryId === CANADA_COUNTRY_CODE;

  const selectedCountryPhoneCode = useMemo(() => (
    PHONE_PREFIX_DATA.find(country => country[2] === prefix) || ['', '', '']
  ), [prefix, PHONE_PREFIX_DATA]);

  const computedPhonePrefixes = useMemo(() => {
    if (!isCanadaUser) {
      // Check with Product: we can add back all the phone prefixes and apply this logic
      // so we show only the flag of the selected "country"
      return PHONE_PREFIX_DATA.filter(phonePrefix => phonePrefix[1] !== 'ca');
    }

    return PHONE_PREFIX_DATA;
  }, [isCanadaUser, PHONE_PREFIX_DATA]);

  const [phonePrefix, setPhonePrefix] = useState(selectedCountryPhoneCode[2]);

  const onInputChange = useCallback((e) => {
    onPropertyChange({
      name,
      value: e.target.value,
      metadata: {
        prefix: phonePrefix,
        abslength
      }
    });
  }, [phonePrefix]);

  const onPhonePrefixSelect = useCallback(({ value: prefixData }) => {
    const { prefix: prefixUpdate, abslength: abslengthUpdate } = prefixData;

    setPhonePrefix(prefixUpdate);

    onPropertyChange({
      name,
      value,
      metadata: {
        prefix: prefixUpdate,
        abslength: abslengthUpdate
      }
    }, { validate: true });
  });

  const onInputBlurFunction = useCallback((e) => {
    onInputBlur({
      name,
      value: e.target.value,
      metadata: {
        prefix: phonePrefix,
        abslength
      }
    });
  });

  useEffect(() => {
    setPhonePrefix(selectedCountryPhoneCode[2]);
  }, [selectedCountryPhoneCode]);

  const mainClass = classNames({
    [styles.main]: true,
    [className]: !!className,
    [styles[`main--${size}`]]: !!size,
    [styles['main--inline']]: inline,
    [styles['main--fullWidth']]: fullWidth,
    [styles[`main--${theme}`]]: !!theme
  });

  const labelClass = classNames({
    [styles.label]: true,
    [className]: !!className,
  });

  const inputError = !!error && (
    <div className={styles.containerMessages}>
      <span className={styles.errorMessage} data-hook={`${dataHook}-error`}>
        {error}
      </span>
    </div>
  );

  const inputClass = classNames({
    [styles.input]: true,
    [styles['input--error']]: !!error,
    [styles.borderGrey]: borderFill
  });

  return (
    <div className={mainClass}>
      <div className={styles.property}>
        <label htmlFor={name} className={labelClass}>{label}</label>
        <div className={styles.inputsContainer}>
          <DropdownField
            inline
            isPhoneNumber
            fullWidth
            textAlign="right"
            alignOptions="right"
            name="select-phone-prefix"
            dataHook="register-phone-number-prefix"
            theme={theme}
            value={phonePrefix}
            items={computedPhonePrefixes.map(([countryName, country, prfx, phoneLength]) => ({
              id: country,
              name: prfx !== 'Others' ? `${prfx} - ${countryName}` : `${countryName}`,
              prefix: prfx,
              abslength: phoneLength
            }))}
            className={styles.dropdownField}
            onPropertyChange={onPhonePrefixSelect}
          />
          <input
            id={name}
            data-hook={dataHook}
            className={`${inputClass}`}
            data-field-name={name}
            name={name}
            type="tel"
            value={value}
            placeholder={placeholder}
            autoComplete="tel-local"
            disabled={disabled}
            onBlur={(e) => onInputBlurFunction(e)}
            onChange={(e) => onInputChange(e)}
          />
        </div>
        {inputError}
      </div>
    </div>
  );
};

PhoneNumberField.propTypes = {
  onPropertyChange: PropTypes.func.isRequired,
  onInputBlur: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  abslength: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.number
  ]),
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  size: PropTypes.oneOf(['normal', 'small']),
  inline: PropTypes.bool,
  countryId: PropTypes.number,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  theme: PropTypes.oneOf(['dark', 'light']),
  className: PropTypes.string,
  prefix: PropTypes.string,
  dataHook: PropTypes.string,
  borderFill: PropTypes.bool,
};

export default PhoneNumberField;
